/**
 * Manages the right-pane selection for the Voluum Standard paths workspace,
 * keeping indices in-bounds when paths/groups change shape.
 */

import { useCallback, useEffect, useState } from 'react';
import type { VoluumFlowUpdate } from '@trafficpulse/shared';

import {
  defaultPathSelection,
  type PathsSelection,
} from './paths-selection';

export interface UsePathsSelectionResult {
  selection: PathsSelection;
  setSelection: (next: PathsSelection) => void;
}

function clamp(value: number, max: number): number {
  return Math.min(Math.max(0, value), Math.max(0, max));
}

function reconcile(
  current: PathsSelection,
  draft: VoluumFlowUpdate,
): PathsSelection {
  const paths = draft.defaultPaths ?? [];
  const groups = draft.conditionalPathsGroups ?? [];

  if (current.kind === 'default-path') {
    if (paths.length === 0) return defaultPathSelection(0);
    return {
      kind: 'default-path',
      index: clamp(current.index, paths.length - 1),
    };
  }
  const group = groups[current.groupIndex];
  if (!group) return defaultPathSelection(0);
  if (current.kind === 'group-conditions') return current;
  const groupPaths = group.paths ?? [];
  if (groupPaths.length === 0) {
    return { kind: 'group-conditions', groupIndex: current.groupIndex };
  }
  return {
    kind: 'group-path',
    groupIndex: current.groupIndex,
    pathIndex: clamp(current.pathIndex, groupPaths.length - 1),
  };
}

export function usePathsSelection(
  draft: VoluumFlowUpdate,
): UsePathsSelectionResult {
  const [selection, setSelection] = useState<PathsSelection>(
    defaultPathSelection(0),
  );

  useEffect(() => {
    setSelection((prev) => reconcile(prev, draft));
  }, [draft]);

  const setSafe = useCallback((next: PathsSelection) => {
    setSelection(next);
  }, []);

  return { selection, setSelection: setSafe };
}
